import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CATEGORIES, CATEGORY_KEYS, ICON_KEYS, IconFor, DAYS } from "@/lib/constants";
import { Trash2, Repeat } from "lucide-react";

const empty = {
  days: ["mon"], start_time: "09:00", end_time: "10:00",
  title: "", icon: "star", category: "routine", notes: "", reminder: false,
};

export default function ActivityDialog({ open, onOpenChange, initial, onSave, onDelete }) {
  const [form, setForm] = useState(empty);
  const [applyToSeries, setApplyToSeries] = useState(false);

  const isEdit = Boolean(initial?.id);
  const hasSeries = Boolean(initial?.series_id);

  useEffect(() => {
    if (!open) return;
    if (initial?.id) {
      // editing existing: prefill from the single activity
      setForm({
        days: [initial.day],
        start_time: initial.start_time,
        end_time: initial.end_time,
        title: initial.title,
        icon: initial.icon || "star",
        category: initial.category || "routine",
        notes: initial.notes || "",
        reminder: !!initial.reminder,
      });
      setApplyToSeries(false);
    } else {
      // new: allow default day override from `initial.day`
      setForm({ ...empty, days: initial?.day ? [initial.day] : ["mon"] });
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDay = (k) =>
    setForm((f) => ({ ...f, days: f.days.includes(k) ? f.days.filter((d) => d !== k) : [...f.days, k] }));

  const handleSave = () => {
    if (isEdit) {
      // edit sends the current single activity's fields; day comes from days[0]
      const patch = {
        day: form.days[0],
        start_time: form.start_time,
        end_time: form.end_time,
        title: form.title,
        icon: form.icon,
        category: form.category,
        notes: form.notes,
        reminder: form.reminder,
      };
      onSave({ mode: "edit", id: initial.id, scope: applyToSeries && hasSeries ? "series" : "one", patch });
    } else {
      onSave({ mode: "create", payload: { ...form } });
    }
  };

  const handleDelete = () => {
    onDelete({ id: initial.id, scope: applyToSeries && hasSeries ? "series" : "one" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="activity-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit activity" : "Add a new activity"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Change the details below." : "Pick one day, or several to repeat every week."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">What are you doing?</Label>
            <Input
              id="title" data-testid="activity-title-input"
              value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="Eat breakfast" className="mt-1 rounded-xl h-11"
            />
          </div>

          <div>
            <Label>{isEdit ? "Day" : "Repeat on"}</Label>
            <div className="grid grid-cols-7 gap-1 mt-1">
              {DAYS.map((d) => {
                const active = form.days.includes(d.key);
                return (
                  <button
                    type="button" key={d.key}
                    data-testid={`day-toggle-${d.key}`}
                    onClick={() => {
                      if (isEdit) set("days", [d.key]);
                      else toggleDay(d.key);
                    }}
                    className={`h-11 rounded-xl border text-xs font-semibold ${active ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  >
                    {d.label.slice(0, 3)}
                  </button>
                );
              })}
            </div>
            {!isEdit && form.days.length > 1 && (
              <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
                <Repeat className="w-3 h-3" /> This will repeat every week on {form.days.length} days.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start">Start</Label>
              <Input id="start" type="time" data-testid="activity-start-input" value={form.start_time}
                onChange={(e) => set("start_time", e.target.value)} className="mt-1 rounded-xl h-11" />
            </div>
            <div>
              <Label htmlFor="end">End</Label>
              <Input id="end" type="time" data-testid="activity-end-input" value={form.end_time}
                onChange={(e) => set("end_time", e.target.value)} className="mt-1 rounded-xl h-11" />
            </div>
          </div>

          <div>
            <Label>Kind of activity</Label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {CATEGORY_KEYS.map((k) => {
                const c = CATEGORIES[k];
                const active = form.category === k;
                return (
                  <button
                    type="button" key={k}
                    data-testid={`category-${k}`}
                    onClick={() => set("category", k)}
                    className={`text-xs py-2 px-2 rounded-xl border ${c.bg} ${c.text} ${active ? "ring-2 ring-amber-400 border-transparent" : c.border}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Icon</Label>
            <div className="mt-1 grid grid-cols-8 gap-2">
              {ICON_KEYS.map((k) => (
                <button
                  type="button" key={k}
                  data-testid={`icon-${k}`}
                  onClick={() => set("icon", k)}
                  className={`h-10 rounded-xl border flex items-center justify-center ${form.icon === k ? "bg-amber-100 border-amber-400" : "bg-white border-slate-200 hover:border-slate-300"}`}
                >
                  <IconFor name={k} className="w-4 h-4 text-slate-700" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea id="notes" data-testid="activity-notes-input" value={form.notes}
              onChange={(e) => set("notes", e.target.value)} className="mt-1 rounded-xl" rows={2}
              placeholder="Anything you want to remember." />
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
            <div>
              <div className="text-sm font-medium text-slate-800">Remind me</div>
              <div className="text-xs text-slate-500">Get a gentle browser notification 5 minutes before it starts.</div>
            </div>
            <Switch checked={form.reminder} onCheckedChange={(v) => set("reminder", v)} data-testid="activity-reminder-switch" />
          </div>

          {isEdit && hasSeries && (
            <label className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl cursor-pointer" data-testid="apply-series-toggle">
              <input
                type="checkbox"
                checked={applyToSeries}
                onChange={(e) => setApplyToSeries(e.target.checked)}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-semibold text-emerald-900">Apply to the whole routine</div>
                <div className="text-xs text-emerald-800">This activity repeats every week. Turn on to change (or delete) every day at once.</div>
              </div>
            </label>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:justify-between">
          {isEdit ? (
            <Button variant="outline" onClick={handleDelete} data-testid="activity-delete-btn" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="activity-cancel-btn" className="rounded-full">Cancel</Button>
            <Button
              onClick={handleSave}
              data-testid="activity-save-btn"
              disabled={!form.title.trim() || form.days.length === 0}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            >
              {isEdit ? "Save changes" : "Add activity"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
